import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { QuoteIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { clientLogos } from '../../data/clientLogos';
export function ClassicProof() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-762" data-mp="1o3g1x8i19f3n">
      <div className="absolute inset-0 pointer-events-none" data-id="element-763" data-mp="1o1dg70nwy84q">
        <div className="absolute top-1/4 right-0 w-[450px] h-[450px] rounded-full bg-primary-50/40 blur-3xl hidden sm:block" data-id="element-764" data-mp="195nqarflxauq" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-cyan-50/25 blur-3xl hidden sm:block" data-id="element-765" data-mp="1e7mug3j68bsq" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-766" data-mp="2qwie3s2gpr7">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-767" data-mp="1gso3nhydndjw">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-768" data-mp="1pu4c3k5mt5lh">
            {lang === 'EN' ? 'Track record' : 'Досвід у цифрах'}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight max-w-4xl mx-auto" data-id="element-769" data-mp="1zrsbwoi3h3ai">
            {c.proof.title}
          </h2>
        </motion.div>

        <dl className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-770" data-mp="28kcaxq2at9vv">
          {c.proof.stats.map((s, i) => <motion.div key={s.label} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08 + i * 0.07
        }} className="p-5 lg:p-6 rounded-2xl bg-navy-50/70 border border-navy-200 text-center last:col-span-2 lg:last:col-span-1" data-id="element-771" data-mp="24i8ok6e3ckvt" data-mpi={i}>
              <dt className="text-2xl lg:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-772" data-mp="1f7m2z6xuprjn">
                {s.value}
              </dt>
              <dd className="mt-2 text-xs sm:text-sm text-navy-500 leading-snug" data-id="element-773" data-mp="1k5nwymwtfb9w">
                {s.label}
              </dd>
            </motion.div>)}
        </dl>

        {/* Logos */}
        <div className="mt-20" data-id="element-774" data-mp="1tvgwuqnli1yb">
          <p className="text-center text-sm font-semibold text-navy-400 tracking-wide uppercase" data-id="element-775" data-mp="1tw8ue70r69r8">
            {c.proof.logosTitle}
          </p>
          <motion.ul initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mt-8 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-776" data-mp="22wour7m790rv">
            {clientLogos.map(logo => <li key={logo.name} className={`group flex items-center justify-center h-24 px-5 rounded-2xl border border-navy-200 ${logo.bgClass === 'bg-navy-900' ? 'bg-navy-900' : 'bg-white'}`} data-id="element-777" data-mp="dhh17rvs8ygv">
                <img src={logo.logo} alt={logo.name} loading="lazy" className="max-h-10 w-auto object-contain grayscale opacity-60 transition-[filter,opacity] duration-200 ease-out group-hover:grayscale-0 group-hover:opacity-100" data-id="element-778" data-mp="3se2h0f446myw" />
              </li>)}
          </motion.ul>
        </div>

        {/* Quotes */}
        <div className="mt-20" data-id="element-779" data-mp="eywe1gmtq6fs">
          <p className="text-center text-sm font-semibold text-navy-400 tracking-wide uppercase" data-id="element-780" data-mp="exus2h49rzja">
            {c.proof.quotesTitle}
          </p>
          <div className="mt-8 grid lg:grid-cols-3 gap-5 lg:gap-6" data-id="element-781" data-mp="2zpoh1ttgtgkp">
            {c.proof.quotes.map((q, i) => <motion.figure key={q.author} initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.25 + i * 0.08
          }} className="flex flex-col p-6 lg:p-7 rounded-2xl bg-navy-50/60 border border-navy-200" data-id="element-782" data-mp="2vnjrb665143c" data-mpi={i}>
                <QuoteIcon className="w-6 h-6 text-primary-300" data-id="element-783" data-mp="df4cpnyt29oi" />
                <blockquote className="mt-4 text-[0.9375rem] text-navy-700 leading-relaxed" data-id="element-784" data-mp="ih210wm2buob">
                  {q.quote}
                </blockquote>
                <figcaption className="mt-auto pt-5 text-sm font-semibold text-navy-900" data-id="element-785" data-mp="1b4xttq8ih2ft">
                  {q.author}
                </figcaption>
              </motion.figure>)}
          </div>
        </div>
      </div>
    </section>;
}